import { useCallback, useMemo, useRef, useState } from "react";

import { useConnections } from "../../state/connections";
import { useActiveOrgId } from "../../state/orgs";
import { toastManager } from "../ui/toast";

export interface PluginAccountActions {
  readonly isBusy: (connectionId: string) => boolean;
  readonly onConnect: (connectionId: string) => void;
  readonly onDisconnect: (connectionId: string) => void;
  readonly onReconnect: (connectionId: string) => void;
}

const actionKey = (orgId: string | null, connectionId: string) =>
  JSON.stringify([orgId, connectionId]);

/** Shared account controls for the catalogue and detail page. */
export function usePluginConnections() {
  const { connect, disconnect, reconnect, ...view } = useConnections();
  const orgId = useActiveOrgId();
  const inFlight = useRef(new Set<string>());
  const [pending, setPending] = useState<ReadonlySet<string>>(() => new Set());

  const run = useCallback(
    async (id: string, action: (id: string) => Promise<void>, failure: string) => {
      const key = actionKey(orgId, id);
      if (inFlight.current.has(key)) return;
      inFlight.current.add(key);
      setPending(new Set(inFlight.current));
      try {
        // Invoke immediately: opening OAuth consent must stay in the click gesture.
        await action(id);
      } catch (error) {
        toastManager.add({
          type: "error",
          title: failure,
          description: error instanceof Error ? error.message : undefined,
        });
      } finally {
        inFlight.current.delete(key);
        setPending(new Set(inFlight.current));
      }
    },
    [orgId],
  );

  const actions = useMemo<PluginAccountActions>(
    () => ({
      isBusy: (id) => pending.has(actionKey(orgId, id)),
      onConnect: (id) => void run(id, connect, "Could not start the connection"),
      onDisconnect: (id) => void run(id, disconnect, "Could not disconnect"),
      onReconnect: (id) => void run(id, reconnect, "Could not reconnect"),
    }),
    [orgId, pending, run, connect, disconnect, reconnect],
  );

  return { ...view, actions };
}
